/**
 * Subclass of ScalarAnimation that has keyframes with linear interpolation.
 *
 * Each keyframe has a fraction [0-1] and a value. Between keyframes, linear
 * interpolation is performed from the prior keyframe to the following keyframe.
 *
 * TypeScript port of C++ ScalarLinearKeyframeAnimation from
 * tabanim/public/animation/ScalarLinearKeyframeAnimation.h
 */

import { KeyframeFractionUtils } from './KeyframeFractionUtils';
import { ScalarAnimation } from './ScalarAnimation';

/**
 * Represents a single keyframe with fraction and value.
 */
export interface Keyframe {
  fraction: number;
  value: number;
}

export class ScalarLinearKeyframeAnimation extends ScalarAnimation {
  private keyframes: Keyframe[] = [];

  constructor() {
    super();
  }

  /**
   * Add a keyframe value at the specified fraction.
   * Replaces any keyframe that already exists at that fraction.
   * Clamps the incoming fraction to [0, 1].
   * Ensures keyframes remain sorted by fraction (monotonically increasing).
   */
  public addKeyframe(fraction: number, value: number): void {
    fraction = Math.max(0, Math.min(1, fraction));

    let inserted = false;
    for (let i = 0; i < this.keyframes.length; i++) {
      if (this.keyframes[i].fraction === fraction) {
        this.keyframes[i].value = value;
        inserted = true;
        break;
      } else if (fraction < this.keyframes[i].fraction) {
        this.keyframes.splice(i, 0, { fraction, value });
        inserted = true;
        break;
      }
    }

    if (!inserted) {
      this.keyframes.push({ fraction, value });
    }
  }

  public removeKeyframe(fraction: number): void {
    const index = this.keyframes.findIndex((kf) => kf.fraction === fraction);
    if (index !== -1) {
      this.keyframes.splice(index, 1);
    }
  }

  public clearKeyframes(): void {
    this.keyframes = [];
  }

  public getKeyframes(): Keyframe[] {
    return this.keyframes.map((kf) => ({ ...kf }));
  }

  public update(): void {
    if (this.keyframes.length === 0) {
      return;
    }

    const fraction = this.getFraction();
    let kfIndex = KeyframeFractionUtils.getFractionArrayIndexBeforeOrAt(this.keyframes, fraction);

    if (kfIndex === -1) {
      kfIndex = 0;
    }

    let newValue: number;

    if (kfIndex >= this.keyframes.length - 1) {
      newValue = this.keyframes[kfIndex].value;
    } else {
      const kf1 = this.keyframes[kfIndex];
      const kf2 = this.keyframes[kfIndex + 1];
      const segmentFraction = KeyframeFractionUtils.mapFractionToInterval(this.keyframes, fraction, kfIndex, kfIndex + 1);
      newValue = kf1.value + (kf2.value - kf1.value) * segmentFraction;
    }

    this.setValue(newValue);
  }
}
